import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import DotGrid from './DotGrid';
import Navbar from './components/Navbar';
import './CoursesPage.css';

const IS_BOT = typeof navigator !== 'undefined' && (
  /Lighthouse|PageSpeed|Googlebot|Chrome-Lighthouse|PTST|HeadlessChrome|Moto G Power/i.test(navigator.userAgent) ||
  navigator.webdriver ||
  window._phantom || window.callPhantom || window.__nightmare
);

export default function CartPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentBanner, setPaymentBanner] = useState(null);

  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    checkUser();

    // Load cart from local storage
    const storedCart = localStorage.getItem('engimitra_cart');
    if (storedCart) {
      try {
        setCartItems(JSON.parse(storedCart));
      } catch (e) {
        console.error("Failed to parse cart");
      }
    }

    // Most real payments (any card — 3D Secure/AFA is legally mandatory for
    // Indian cards) redirect the whole browser away from this page and back,
    // via api/verify-payment-redirect.js, rather than resolving in-page. That
    // endpoint has already verified the signature and recorded the purchase
    // server-side by the time we land back here — this just handles the UX.
    // Uses a non-blocking banner rather than alert(): a blocking native
    // dialog fired synchronously on mount stalls the whole page (including
    // the pending navigate() below) until a human dismisses it.
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    if (paymentStatus === 'success') {
      localStorage.removeItem('engimitra_cart');
      setCartItems([]);
      setPaymentBanner({ type: 'success', message: 'Payment successful! Your resources have been unlocked.' });
      const t = setTimeout(() => navigate('/account', { replace: true }), 1800);
      return () => clearTimeout(t);
    } else if (paymentStatus) {
      setPaymentBanner({ type: 'error', message: `Payment was not completed: ${decodeURIComponent(paymentStatus)}` });
      navigate('/cart', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setUser(session?.user || null);
    setLoading(false);
    
    // Auth Listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });
    return () => subscription?.unsubscribe();
  };

  const removeItem = (id) => {
    const updatedCart = cartItems.filter(item => item.id !== id);
    setCartItems(updatedCart);
    localStorage.setItem('engimitra_cart', JSON.stringify(updatedCart));
  };

  // --- Calculate Totals ---
  const subtotal = cartItems.reduce((acc, item) => acc + item.price, 0);
  const discount = cartItems.reduce((acc, item) => acc + (item.original_price - item.price), 0);
  const total = subtotal; // Assuming prices include tax or calculating it separately if needed.

  // --- Razorpay Integration ---
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!user) {
      alert("Please login to proceed with the payment.");
      navigate('/login');
      return;
    }
    
    setIsProcessingPayment(true);
    const res = await loadRazorpayScript();

    if (!res) {
      alert("Razorpay SDK failed to load. Are you offline?");
      setIsProcessingPayment(false);
      return;
    }

    const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_YOUR_KEY_ID_HERE";

    // --- Mock Payment Fallback for Testing ---
    // Only used when no real Razorpay key is configured — this path does NOT
    // record a purchase (there's no payment to verify), it just simulates the
    // UX for local development.
    if (!razorpayKey || razorpayKey === "rzp_test_YOUR_KEY_ID_HERE") {
      setTimeout(() => {
        alert("TEST MODE: Payment simulated successfully!\n(Add your real REACT_APP_RAZORPAY_KEY_ID + server-side RAZORPAY_KEY_SECRET to open the actual payment gateway).");
        setCartItems([]);
        localStorage.removeItem('engimitra_cart');
        setIsProcessingPayment(false);
        navigate('/account');
      }, 1500);
      return;
    }

    // The order amount is computed server-side from the DB (never trusted
    // from the client), so a tampered `total` here can't affect what's charged.
    let orderData;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const orderRes = await fetch('/api/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ items: cartItems.map(item => ({ id: item.id, item_type: item.item_type })) }),
      });
      orderData = await orderRes.json();
      if (!orderRes.ok) throw new Error(orderData.error || 'Failed to create order');
    } catch (err) {
      alert(`Could not start checkout: ${err.message}`);
      setIsProcessingPayment(false);
      return;
    }

    const options = {
      key: razorpayKey,
      amount: orderData.amount,
      currency: orderData.currency,
      order_id: orderData.orderId,
      name: "ENGImitra",
      description: "Premium Course & Note Purchase",
      image: "/engimitra-logo-512.png",
      // Using callback_url + redirect (rather than an in-page `handler`) is
      // deliberate and required, not just Razorpay's suggested default: 3D
      // Secure/AFA is mandatory for Indian card payments, which means the
      // browser leaves this page entirely for the bank's auth step on most
      // real transactions. A JS handler callback never fires once that
      // happens, so relying on it alone would silently fail to record the
      // large majority of real purchases. api/verify-payment-redirect.js
      // verifies the signature and records the purchase server-side, then
      // redirects back here with a status in the query string (handled in
      // the effect above).
      callback_url: `${window.location.origin}/api/verify-payment-redirect`,
      redirect: true,
      prefill: {
        name: user.user_metadata?.full_name || "Engineering Student",
        email: user.email,
        contact: user.user_metadata?.phone || "9999999999",
      },
      theme: {
        color: "#00e5c8",
      },
    };

    try {
      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (e) {
      alert("Error opening Razorpay. Please check your API key.");
    }
    setIsProcessingPayment(false);
  };

  if (loading) {
    return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}><p style={{ color: 'var(--muted)' }}>Loading cart...</p></div>;
  }

  return (
    <div className="courses-page-container document-explorer-page">
      {paymentBanner && (
        <div
          role="status"
          style={{
            position: 'fixed', top: '1rem', left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
            padding: '0.9rem 1.5rem', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600,
            color: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
            background: paymentBanner.type === 'success' ? 'var(--cyan, #00e5c8)' : 'var(--rose, #ff4d6d)',
          }}
        >
          {paymentBanner.message}
        </div>
      )}
      <div className="cp-bg-wrapper">
        {!IS_BOT && (
          <DotGrid
            dotSize={2}
            gap={15}
            baseColor="#004f54"
            activeColor="#00e5c8"
            proximity={120}
            shockRadius={250}
            shockStrength={5}
            resistance={750}
            returnDuration={1.5}
          />
        )}
      </div>

      <Navbar />

      <main className="cp-main" style={{ paddingBottom: '6rem' }}>
        <section className="cp-header-section" style={{ paddingBottom: '2rem' }}>
          <div>
            <h1 className="cp-title"><span className="cp-gradient-text">Shopping</span> Cart</h1>
            <p className="cp-subtitle">Review your items and proceed to secure checkout.</p>
          </div>
        </section>

        {cartItems.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', width: '100%' }}>
            <div className="glass-panel" style={{ padding: '4rem 2rem', textAlign: 'center', borderRadius: '24px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '4.5rem', color: 'var(--border)', marginBottom: '1.5rem' }}>remove_shopping_cart</span>
              <h3 style={{ fontSize: '1.6rem', color: 'var(--ink)', marginBottom: '0.75rem' }}>Your cart is empty</h3>
              <p style={{ color: 'var(--muted)', marginBottom: '2.5rem', fontSize: '0.95rem', lineHeight: '1.6' }}>Looks like you haven't added any resources yet. Start exploring our high-quality courses and notes!</p>
              <Link to="/courses" className="cp-btn cp-cart-empty-cta" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 2rem' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>search</span>
                Explore Courses
              </Link>
            </div>
          </div>
        ) : (
          <div className="cp-layout">
            {/* Cart Items List */}
            <div style={{ flexGrow: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {cartItems.map((item) => (
                  <div key={item.id} className="glass-panel cp-cart-item" style={{ display: 'flex', gap: '1.5rem', padding: '1.5rem', borderRadius: '16px', alignItems: 'center' }}>
                    <div className="cp-cart-item-thumb" style={{ width: '120px', height: '80px', borderRadius: '8px', background: `url(${item.thumbnail_url}) center/cover`, border: '1px solid var(--border)' }}></div>
                    <div className="cp-cart-item-content" style={{ flexGrow: 1, minWidth: 0 }}>
                      <span style={{ textTransform: 'uppercase', fontSize: '0.7rem', color: 'var(--cyan)', fontWeight: 'bold', letterSpacing: '1px' }}>{item.item_type}</span>
                      <h3 style={{ color: 'var(--ink)', fontSize: '1.1rem', marginTop: '0.2rem', marginBottom: '0.5rem', overflowWrap: 'anywhere' }}>{item.title}</h3>
                      <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', color: 'var(--rose)', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem', padding: 0, flexWrap: 'wrap' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>delete</span> Remove
                      </button>
                    </div>
                    <div className="cp-cart-item-price" style={{ textAlign: 'right', flexShrink: 0 }}>
                      <p style={{ color: 'var(--ink)', fontSize: '1.2rem', fontWeight: 'bold' }}>₹{item.price}</p>
                      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', textDecoration: 'line-through' }}>₹{item.original_price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Checkout Summary */}
            <aside className="cp-sidebar cp-cart-summary">
              <div className="glass-panel cp-cart-summary-panel" style={{ position: 'sticky', top: '100px', padding: '2rem', borderRadius: '24px' }}>
                <h3 style={{ fontSize: '1.2rem', color: 'var(--ink)', marginBottom: '1.5rem', borderBottom: '1px solid var(--border)', paddingBottom: '1rem' }}>Order Summary</h3>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <span style={{ color: 'var(--muted)' }}>Subtotal</span>
                  <span style={{ color: 'var(--ink)', fontWeight: 'bold' }}>₹{subtotal + discount}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <span style={{ color: 'var(--muted)' }}>Discount</span>
                  <span style={{ color: 'var(--cyan)', fontWeight: 'bold' }}>- ₹{discount}</span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px dashed var(--border)' }}>
                  <span style={{ color: 'var(--ink)', fontSize: '1.2rem', fontWeight: 'bold' }}>Total</span>
                  <span style={{ color: 'var(--ink)', fontSize: '1.5rem', fontWeight: 'bold' }}>₹{total}</span>
                </div>

                <button 
                  onClick={handlePayment} 
                  disabled={isProcessingPayment}
                  className="cp-btn" 
                  style={{ width: '100%', marginTop: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', opacity: isProcessingPayment ? 0.7 : 1 }}
                >
                  {isProcessingPayment ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">lock</span>
                      Proceed to Pay
                    </>
                  )}
                </button>
                
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem', color: 'var(--muted)', fontSize: '0.8rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>verified_user</span>
                  Secured by Razorpay
                </div>
              </div>
            </aside>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="em-footer">
        <div className="fg">
          <div className="fb">
            <a href="/" className="em-logo"><span className="l1">ENGI</span><span className="l2">&lt;mitra&gt;</span></a>
            <p>Built for 1st year engineering students. Expert notes, courses, and MPQs for every major Indian university — since 2022.</p>
          </div>
          <div className="fc">
            <h5>Platform</h5>
            <ul>{["Courses", "Notes", "MPQs"].map(l => <li key={l}><Link to={l === 'Courses' ? '/courses' : `/${l.toLowerCase()}`}>{l}</Link></li>)}</ul>
          </div>
          <div className="fc">
            <h5>Legal</h5>
            <ul>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </div>
        </div>
        <div className="fb-bottom">
          <p>© 2026 ENGImitra. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
